document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const FREE_SHIPPING_THRESHOLD = 7500;
const shopRoot = window.Shopify?.routes?.root || '/';
let activeOverlay = null;
let activeOverlayTrigger = null;

const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  "'": '&#39;',
  '"': '&quot;',
}[character]));

const formatMoney = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(cents || 0) / 100);

const focusableElements = (container) => Array.from(container.querySelectorAll(
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
)).filter((element) => !element.hidden && element.offsetParent !== null);

function openOverlay(overlay, trigger) {
  if (!overlay) return;
  if (activeOverlay && activeOverlay !== overlay) closeOverlay(activeOverlay, false);

  activeOverlay = overlay;
  activeOverlayTrigger = trigger || document.activeElement;
  overlay.hidden = false;
  document.body.classList.add('is-locked');

  window.requestAnimationFrame(() => {
    const preferredFocus = overlay.querySelector('input:not([type="hidden"]), [data-overlay-initial-focus]');
    const firstFocusable = focusableElements(overlay)[0];
    (preferredFocus || firstFocusable)?.focus();
  });
}

function closeOverlay(overlay = activeOverlay, restoreFocus = true) {
  if (!overlay) return;
  overlay.hidden = true;
  if (overlay === activeOverlay) activeOverlay = null;
  document.body.classList.remove('is-locked');
  if (restoreFocus) activeOverlayTrigger?.focus();
  activeOverlayTrigger = null;
}

function trapFocus(event, container) {
  if (!container || event.key !== 'Tab') return;
  const focusable = focusableElements(container);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function trapOverlayFocus(event) {
  trapFocus(event, activeOverlay);
}

function openMobileMenu(toggle) {
  const menu = document.querySelector('[data-mobile-menu]');
  if (!menu) return;
  menu.hidden = false;
  toggle?.setAttribute('aria-expanded', 'true');
  document.body.classList.add('is-locked');
  activeOverlayTrigger = toggle;
  menu.querySelector('a, button')?.focus();
}

function closeMobileMenu(restoreFocus = true) {
  const menu = document.querySelector('[data-mobile-menu]');
  const toggle = document.querySelector('[data-mobile-menu-toggle]');
  if (!menu) return;
  menu.hidden = true;
  toggle?.setAttribute('aria-expanded', 'false');
  document.body.classList.remove('is-locked');
  if (restoreFocus) toggle?.focus();
}

function applyGalleryImage(image, media, sizes = media.sizes) {
  if (!image || !media) return;

  if (sizes) image.setAttribute('sizes', sizes);
  if (media.srcset) image.setAttribute('srcset', media.srcset);
  else image.removeAttribute('srcset');
  if (media.src) image.src = media.src;
  if (media.width) image.width = Number(media.width);
  if (media.height) image.height = Number(media.height);
  image.alt = media.alt || '';
  image.setAttribute('data-media-id', String(media.id));
}

function setGalleryImage(section, mediaId) {
  const gallery = section?.querySelector('[data-product-gallery]');
  gallery?.galleryController?.selectMedia(String(mediaId));
}

function setupProductGallery(gallery) {
  const mediaElement = gallery.querySelector('[data-product-media]');
  const mainWrapper = gallery.querySelector('[data-product-main-image]');
  const mainImage = mainWrapper?.querySelector('img');
  const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
  const announcer = gallery.querySelector('[data-gallery-announcer]');
  const dialog = gallery.querySelector('[data-gallery-dialog]');
  const dialogImage = dialog?.querySelector('[data-gallery-dialog-image]');
  const dialogStatus = dialog?.querySelector('[data-gallery-dialog-status]');
  const dialogClose = dialog?.querySelector('[data-gallery-dialog-close]');

  if (!mediaElement || !mainWrapper || !mainImage) return;

  let media = [];
  try {
    media = JSON.parse(mediaElement.textContent);
  } catch (error) {
    return;
  }
  if (media.length === 0) return;

  const mediaById = new Map(media.map((item) => [String(item.id), item]));
  const initialMediaId = String(mainWrapper.dataset.currentMediaId || media[0].id);
  let currentIndex = Math.max(0, media.findIndex((item) => String(item.id) === initialMediaId));
  let dialogTrigger = null;
  let touchStartX = null;
  let touchStartY = null;

  mainImage.setAttribute('data-media-id', String(media[currentIndex].id));

  const updateDialog = (item) => {
    if (!dialog?.open || !dialogImage) return;
    applyGalleryImage(dialogImage, { ...item, src: item.full_src || item.src }, '100vw');
    if (dialogStatus) dialogStatus.textContent = item.status || item.alt;
  };

  const selectMedia = (mediaId, { announce = true } = {}) => {
    const item = mediaById.get(String(mediaId));
    if (!item) return;

    const nextIndex = media.indexOf(item);
    const hasChanged = nextIndex !== currentIndex;
    currentIndex = nextIndex;
    mainWrapper.dataset.currentMediaId = String(item.id);
    applyGalleryImage(mainImage, item);

    thumbs.forEach((thumb) => {
      thumb.setAttribute('aria-current', String(thumb.dataset.mediaId) === String(item.id) ? 'true' : 'false');
    });

    updateDialog(item);
    if (announce && hasChanged && announcer) announcer.textContent = item.status || item.alt;
  };

  const move = (direction, options) => {
    const nextIndex = (currentIndex + direction + media.length) % media.length;
    selectMedia(media[nextIndex].id, options);
  };

  const focusCurrentThumb = () => {
    const currentId = String(media[currentIndex].id);
    thumbs.find((thumb) => String(thumb.dataset.mediaId) === currentId)?.focus();
  };

  const openDialog = (trigger) => {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    dialogTrigger = trigger;
    dialog.showModal();
    document.body.classList.add('is-locked');
    updateDialog(media[currentIndex]);
    window.requestAnimationFrame(() => dialogClose?.focus());
  };

  gallery.galleryController = {
    selectMedia,
    next: () => move(1),
    previous: () => move(-1),
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      if (event.key === 'Home') selectMedia(media[0].id);
      else if (event.key === 'End') selectMedia(media[media.length - 1].id);
      else move(event.key === 'ArrowRight' ? 1 : -1);
      focusCurrentThumb();
    });
  });

  gallery.querySelector('[data-gallery-previous]')?.addEventListener('click', () => move(-1));
  gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => move(1));
  gallery.querySelector('[data-gallery-zoom]')?.addEventListener('click', (event) => openDialog(event.currentTarget));
  dialog?.querySelector('[data-gallery-dialog-previous]')?.addEventListener('click', () => move(-1));
  dialog?.querySelector('[data-gallery-dialog-next]')?.addEventListener('click', () => move(1));
  dialogClose?.addEventListener('click', () => dialog.close());

  dialog?.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      dialog.close();
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
      return;
    }
    trapFocus(event, dialog);
  });

  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  dialog?.addEventListener('close', () => {
    document.body.classList.remove('is-locked');
    dialogTrigger?.focus();
    dialogTrigger = null;
  });

  mainWrapper.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return;
    touchStartX = event.touches[0].clientX;
    touchStartY = event.touches[0].clientY;
  }, { passive: true });

  mainWrapper.addEventListener('touchend', (event) => {
    if (touchStartX === null || touchStartY === null || event.changedTouches.length !== 1) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX;
    const deltaY = event.changedTouches[0].clientY - touchStartY;
    touchStartX = null;
    touchStartY = null;

    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    move(deltaX < 0 ? 1 : -1);
  }, { passive: true });
}

function setupProductForm(form) {
  const variantsElement = form.querySelector('[data-product-variants]');
  const variantSelect = form.querySelector('[data-variant-select]');
  const optionInputs = Array.from(form.querySelectorAll('[data-option-input]'));
  const section = form.closest('[data-product-section]');

  if (!variantsElement || !variantSelect || !section) return;

  let variants = [];
  try {
    variants = JSON.parse(variantsElement.textContent);
  } catch (error) {
    return;
  }

  const selectedOptions = () => {
    const values = [];
    optionInputs.forEach((input) => {
      if (input.tagName !== 'SELECT' && !input.checked) return;
      if (!input.value) return;
      values[Number(input.dataset.optionPosition) - 1] = input.value;
    });
    return values;
  };

  const findSelectedVariant = () => {
    if (optionInputs.length === 0) {
      return variants.find((variant) => String(variant.id) === variantSelect.value);
    }

    const options = selectedOptions();
    if (options.length !== variants[0]?.options.length || options.some((value) => !value)) return null;
    return variants.find((variant) => variant.options.every((value, index) => value === options[index]));
  };

  const updateOptionAvailability = () => {
    if (optionInputs.length === 0) return;
    const currentOptions = selectedOptions();

    optionInputs.forEach((input) => {
      const optionIndex = Number(input.dataset.optionPosition) - 1;
      const isAvailable = (value) => variants.some((variant) => (
        variant.available
        && variant.options[optionIndex] === value
        && variant.options.every((variantValue, index) => (
          index === optionIndex || !currentOptions[index] || variantValue === currentOptions[index]
        ))
      ));

      if (input.tagName === 'SELECT') {
        Array.from(input.options).forEach((option) => {
          if (!option.value) return;
          option.disabled = !isAvailable(option.value);
        });
        return;
      }

      const available = isAvailable(input.value);

      input.disabled = !available;
      const label = form.querySelector(`label[for="${input.id}"]`);
      if (label) label.classList.toggle('is-unavailable', !available);
    });
  };

  const renderVariantMedia = (variant) => {
    if (!variant?.featured_media_id) return;
    setGalleryImage(section, variant.featured_media_id);
  };

  const renderVariant = (variant) => {
    const price = section.querySelector('[data-product-price]');
    const comparePrice = section.querySelector('[data-compare-price]');
    const submit = form.querySelector('[data-add-to-cart]');
    const validation = form.querySelector('[data-variant-validation]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const stickyButton = section.querySelector('[data-sticky-add]');

    if (!variant) {
      const currentOptions = selectedOptions();
      const mediaVariant = variants.find((item) => (
        item.featured_media_id
        && item.options.every((value, index) => !currentOptions[index] || value === currentOptions[index])
      ));
      renderVariantMedia(mediaVariant);

      if (submit) {
        submit.disabled = true;
        submit.textContent = submit.getAttribute('data-select-label');
      }
      if (validation) validation.hidden = false;
      if (stickyButton) stickyButton.disabled = true;
      updateOptionAvailability();
      return;
    }

    variantSelect.value = String(variant.id);
    if (price) price.textContent = variant.price;
    if (stickyPrice) stickyPrice.textContent = variant.price;

    if (comparePrice) {
      comparePrice.hidden = !variant.compare_at_price;
      comparePrice.textContent = variant.compare_at_price || '';
    }

    if (submit) {
      submit.disabled = !variant.available;
      submit.textContent = variant.available
        ? submit.getAttribute('data-add-label')
        : submit.getAttribute('data-sold-out-label');
    }
    if (validation) validation.hidden = true;
    if (stickyButton) stickyButton.disabled = !variant.available;

    renderVariantMedia(variant);

    if (variant.url) window.history.replaceState({}, '', variant.url);
    updateOptionAvailability();
  };

  optionInputs.forEach((input) => {
    input.addEventListener('change', () => renderVariant(findSelectedVariant()));
  });

  variantSelect.addEventListener('change', () => {
    const variant = variants.find((item) => String(item.id) === variantSelect.value);
    renderVariant(variant);
  });

  renderVariant(findSelectedVariant());
}

function productFormError(form, message = '') {
  const error = form.querySelector('[data-product-error]');
  if (!error) return;
  error.textContent = message;
  error.hidden = !message;
}

async function refreshCart() {
  const response = await fetch(`${shopRoot}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Cart request failed');
  const cart = await response.json();
  renderCart(cart);
  return cart;
}

function renderCart(cart) {
  const itemsContainer = document.querySelector('[data-cart-items]');
  const emptyState = document.querySelector('[data-cart-empty]');
  const footer = document.querySelector('[data-cart-footer]');
  const count = document.querySelector('[data-cart-count]');
  const drawerCount = document.querySelector('[data-cart-drawer-count]');
  const subtotal = document.querySelector('[data-cart-subtotal]');
  const progress = document.querySelector('[data-shipping-progress]');
  const shippingMessage = document.querySelector('[data-shipping-message]');
  const live = document.querySelector('[data-cart-live]');

  if (count) {
    count.textContent = cart.item_count;
    count.hidden = cart.item_count === 0;
  }
  if (drawerCount) drawerCount.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';
  if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

  const remaining = FREE_SHIPPING_THRESHOLD - cart.total_price;
  if (shippingMessage) {
    shippingMessage.textContent = remaining > 0
      ? `You're ${formatMoney(remaining)} away from free U.S. shipping.`
      : 'You qualify for free U.S. shipping.';
  }
  if (progress) {
    const width = Math.min(100, (cart.total_price / FREE_SHIPPING_THRESHOLD) * 100);
    progress.style.width = `${width}%`;
  }

  if (itemsContainer) {
    itemsContainer.innerHTML = cart.items.map((item, index) => {
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p>${escapeHTML(item.variant_title)}</p>`
        : '';
      const image = item.image
        ? `<img src="${escapeHTML(item.image)}&width=240" alt="" width="96" height="120">`
        : '';
      return `
        <article class="cart-drawer-item" data-cart-line="${index + 1}">
          <a class="cart-drawer-image" href="${escapeHTML(item.url)}">${image}</a>
          <div class="cart-drawer-item-info">
            <a href="${escapeHTML(item.url)}"><strong>${escapeHTML(item.product_title)}</strong></a>
            ${variant}
            <div class="quantity-stepper">
              <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">&minus;</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button class="text-button" type="button" data-cart-change data-line="${index + 1}" data-quantity="0">Remove</button>
          </div>
          <strong>${formatMoney(item.final_line_price)}</strong>
        </article>`;
    }).join('');
    itemsContainer.hidden = cart.item_count === 0;
  }

  if (emptyState) emptyState.hidden = cart.item_count > 0;
  if (footer) footer.hidden = cart.item_count === 0;
  if (live) live.textContent = `Cart updated. ${cart.item_count} items, subtotal ${formatMoney(cart.total_price)}.`;
}

async function changeCart(line, quantity, button) {
  button?.setAttribute('aria-busy', 'true');
  try {
    const response = await fetch(`${shopRoot}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ line, quantity }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    renderCart(await response.json());
  } catch (error) {
    const live = document.querySelector('[data-cart-live]');
    if (live) live.textContent = 'The cart could not be updated. Please try again.';
  } finally {
    button?.removeAttribute('aria-busy');
  }
}

async function addProductToCart(form, submit) {
  const originalLabel = submit.textContent;
  submit.disabled = true;
  submit.setAttribute('aria-busy', 'true');
  submit.textContent = submit.getAttribute('data-loading-label') || 'Adding...';
  productFormError(form);

  try {
    const response = await fetch(`${shopRoot}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || 'This item could not be added.');
    }
    await refreshCart();
    openOverlay(document.querySelector('[data-cart-drawer]'), submit);
  } catch (error) {
    productFormError(form, error.message);
  } finally {
    submit.removeAttribute('aria-busy');
    submit.disabled = false;
    submit.textContent = originalLabel;
  }
}

function setupPredictiveSearch() {
  const form = document.querySelector('[data-predictive-search-form]');
  const input = form?.querySelector('[data-predictive-search-input]');
  const results = document.querySelector('[data-predictive-results]');
  const status = document.querySelector('[data-predictive-status]');
  if (!form || !input || !results || !status) return;

  const initialHTML = results.innerHTML;
  let timer;
  let controller;

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    controller?.abort();
    const query = input.value.trim();

    if (query.length < 2) {
      status.textContent = '';
      results.innerHTML = initialHTML;
      return;
    }

    timer = window.setTimeout(async () => {
      controller = new AbortController();
      status.textContent = 'Searching...';
      try {
        const parameters = new URLSearchParams({
          q: query,
          'resources[type]': 'product,collection',
          'resources[limit]': '5',
          'resources[options][unavailable_products]': 'last',
        });
        const response = await fetch(`${shopRoot}search/suggest.json?${parameters}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Search failed');
        const data = await response.json();
        const products = data.resources?.results?.products || [];
        const collections = data.resources?.results?.collections || [];

        const productHTML = products.length > 0 ? `
          <div>
            <p class="overlay-label">Products</p>
            <div class="predictive-product-grid">
              ${products.slice(0, 5).map((product) => {
                const imageValue = product.image || product.featured_image;
                const imageURL = typeof imageValue === 'string' ? imageValue : imageValue?.url;
                const image = imageURL ? `<img src="${escapeHTML(imageURL)}&width=480" alt="" width="320" height="400">` : '';
                const rawPrice = String(product.price || '');
                const numericPrice = Number(rawPrice.replace(/[^0-9.-]/g, ''));
                const displayPrice = rawPrice && Number.isFinite(numericPrice)
                  ? formatMoney(Math.round(numericPrice * 100))
                  : rawPrice;
                return `<a class="predictive-product" href="${escapeHTML(product.url)}">${image}<strong>${escapeHTML(product.title)}</strong><span>${escapeHTML(displayPrice)}</span></a>`;
              }).join('')}
            </div>
          </div>` : '';

        const collectionHTML = collections.length > 0 ? `
          <div>
            <p class="overlay-label">Collections</p>
            <div class="search-collection-links">${collections.slice(0, 4).map((collection) => `<a href="${escapeHTML(collection.url)}">${escapeHTML(collection.title)}</a>`).join('')}</div>
          </div>` : '';

        results.innerHTML = `${productHTML}${collectionHTML}<a class="arrow-link predictive-view-all" href="${form.action}?q=${encodeURIComponent(query)}&type=product">View all results for &quot;${escapeHTML(query)}&quot; &rarr;</a>`;
        status.textContent = products.length + collections.length > 0 ? '' : `No suggestions for "${query}".`;
      } catch (error) {
        if (error.name !== 'AbortError') status.textContent = 'Search suggestions are unavailable. Press Enter to search.';
      }
    }, 220);
  });
}

function setupStickyAddToCart() {
  const form = document.querySelector('[data-product-form]');
  const sticky = document.querySelector('[data-sticky-atc]');
  if (!form || !sticky || !('IntersectionObserver' in window)) return;

  sticky.hidden = false;
  const observer = new IntersectionObserver(([entry]) => {
    sticky.classList.toggle('is-visible', !entry.isIntersecting);
  }, { threshold: 0.05 });
  observer.observe(form);
}

document.addEventListener('click', (event) => {
  const mobileToggle = event.target.closest('[data-mobile-menu-toggle]');
  if (mobileToggle) {
    openMobileMenu(mobileToggle);
    return;
  }

  if (event.target.closest('[data-mobile-menu-close]')) {
    closeMobileMenu();
    return;
  }

  if (event.target.closest('[data-mobile-menu] a')) {
    closeMobileMenu(false);
    return;
  }

  const searchOpen = event.target.closest('[data-search-open]');
  if (searchOpen) {
    openOverlay(document.querySelector('[data-search-overlay]'), searchOpen);
    return;
  }

  if (event.target.closest('[data-search-close]')) {
    closeOverlay(document.querySelector('[data-search-overlay]'));
    return;
  }

  const cartOpen = event.target.closest('[data-cart-open]');
  if (cartOpen) {
    openOverlay(document.querySelector('[data-cart-drawer]'), cartOpen);
    return;
  }

  if (event.target.closest('[data-cart-close]')) {
    closeOverlay(document.querySelector('[data-cart-drawer]'));
    return;
  }

  const overlayClose = event.target.closest('[data-overlay-close]');
  if (overlayClose) {
    closeOverlay(overlayClose.closest('[data-search-overlay], [data-cart-drawer]'));
    return;
  }

  const cartChange = event.target.closest('[data-cart-change]');
  if (cartChange) {
    changeCart(Number(cartChange.dataset.line), Number(cartChange.dataset.quantity), cartChange);
    return;
  }

  const thumb = event.target.closest('[data-gallery-thumb]');
  if (thumb) {
    const section = thumb.closest('[data-product-section]');
    if (section) setGalleryImage(section, thumb.dataset.mediaId);
    return;
  }

  const quantityButton = event.target.closest('[data-product-quantity-change]');
  if (quantityButton) {
    const input = quantityButton.closest('[data-product-quantity]')?.querySelector('input');
    if (!input) return;
    const nextValue = Math.max(1, Number(input.value || 1) + Number(quantityButton.dataset.productQuantityChange));
    input.value = nextValue;
    return;
  }

  const stickyAdd = event.target.closest('[data-sticky-add]');
  if (stickyAdd) {
    document.querySelector('[data-product-form]')?.requestSubmit(document.querySelector('[data-add-to-cart]'));
    return;
  }

  const sizeGuideOpen = event.target.closest('[data-size-guide-open]');
  if (sizeGuideOpen) {
    document.querySelector('[data-size-guide-dialog]')?.showModal();
    return;
  }

  const sizeGuideClose = event.target.closest('[data-size-guide-close]');
  if (sizeGuideClose) {
    sizeGuideClose.closest('dialog')?.close();
    return;
  }

  if (event.target.closest('[data-facets-close]')) {
    const facets = document.querySelector('.facets-shell');
    if (facets) facets.open = false;
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-product-form]');
  const submit = event.submitter;
  if (!form || !submit?.matches('[data-add-to-cart]')) return;
  event.preventDefault();
  addProductToCart(form, submit);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    if (!document.querySelector('[data-mobile-menu]')?.hidden) {
      closeMobileMenu();
      return;
    }
    if (activeOverlay) closeOverlay(activeOverlay);
  }
  trapOverlayFocus(event);
});

document.querySelectorAll('[data-product-gallery]').forEach(setupProductGallery);
document.querySelectorAll('[data-product-form]').forEach(setupProductForm);
document.querySelectorAll('[data-sort-select]').forEach((select) => {
  select.addEventListener('change', () => select.form?.submit());
});
setupPredictiveSearch();
setupStickyAddToCart();
